/**
 * WebSocket Presence & Notification Hook
 *
 * Maintains a persistent WebSocket connection for:
 * 1. User presence tracking (scaling engine detects active users)
 * 2. Real-time notification push (job status changes)
 *
 * Security:
 * - Passes Cognito ID token as query parameter (validated by Lambda authorizer)
 * - Fetches fresh token on every connect/reconnect (short-lived JWTs)
 * - No-ops in local dev mode (no token, no connection)
 *
 * Resilience:
 * - Exponential backoff reconnection (1s -> 2s -> 4s -> ... -> 30s cap)
 * - Heartbeat every 5 min to refresh server-side TTL
 * - Auto-disconnect after 10 min tab hidden, reconnect when visible
 * - Graceful cleanup on logout and unmount
 */

import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { isLocalDev } from '../utils/is-local-dev';
import {
    WS_HEARTBEAT_INTERVAL_MS,
    WS_RECONNECT_BASE_MS,
    WS_RECONNECT_MAX_MS,
    WS_VISIBILITY_DISCONNECT_MS,
} from '../config/constants';

const WEBSOCKET_URL = import.meta.env.VITE_WEBSOCKET_URL || '';

async function getIdToken(): Promise<string | null> {
    try {
        const { fetchAuthSession } = await import('aws-amplify/auth');
        const session = await fetchAuthSession();
        return session.tokens?.idToken?.toString() ?? null;
    } catch {
        return null;
    }
}

export function useWebSocket(): void {
    const { isAuthenticated } = useAuth();
    const queryClient = useQueryClient();

    const wsRef = useRef<WebSocket | null>(null);
    const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const visibilityTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const attemptRef = useRef(0);
    const intentionalCloseRef = useRef(false);
    const hiddenAtRef = useRef<number | null>(null);

    const clearTimers = useCallback(() => {
        if (heartbeatRef.current) {
            clearInterval(heartbeatRef.current);
            heartbeatRef.current = null;
        }
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }
        if (visibilityTimeoutRef.current) {
            clearTimeout(visibilityTimeoutRef.current);
            visibilityTimeoutRef.current = null;
        }
    }, []);

    const closeConnection = useCallback(() => {
        intentionalCloseRef.current = true;
        clearTimers();
        if (wsRef.current) {
            wsRef.current.close(1000, 'client_logout');
            wsRef.current = null;
        }
    }, [clearTimers]);

    const connect = useCallback(async () => {
        if (wsRef.current?.readyState === WebSocket.OPEN ||
            wsRef.current?.readyState === WebSocket.CONNECTING) {
            return;
        }

        const token = await getIdToken();
        if (!token) return;

        intentionalCloseRef.current = false;

        const url = `${WEBSOCKET_URL}?token=${encodeURIComponent(token)}`;
        const ws = new WebSocket(url);
        wsRef.current = ws;

        ws.onopen = () => {
            attemptRef.current = 0;

            heartbeatRef.current = setInterval(() => {
                if (ws.readyState === WebSocket.OPEN) {
                    ws.send(JSON.stringify({ action: 'heartbeat' }));
                }
            }, WS_HEARTBEAT_INTERVAL_MS);
        };

        ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                if (data.event_type === 'job.status.changed') {
                    queryClient.invalidateQueries({ queryKey: ['jobs'] });
                    queryClient.invalidateQueries({ queryKey: ['notifications'] });
                }
            } catch {
                // Non-JSON message (e.g. pong frame) — ignore
            }
        };

        ws.onclose = () => {
            if (heartbeatRef.current) {
                clearInterval(heartbeatRef.current);
                heartbeatRef.current = null;
            }
            wsRef.current = null;

            if (!intentionalCloseRef.current) {
                const delay = Math.min(
                    WS_RECONNECT_BASE_MS * Math.pow(2, attemptRef.current),
                    WS_RECONNECT_MAX_MS,
                );
                attemptRef.current += 1;
                reconnectTimeoutRef.current = setTimeout(() => {
                    connect();
                }, delay);
            }
        };

        ws.onerror = () => {
            // Errors trigger onclose — reconnection handled there
        };
    }, [queryClient]);

    // Connect on auth, disconnect on logout
    useEffect(() => {
        if (isLocalDev() || !WEBSOCKET_URL) return;

        if (isAuthenticated) {
            connect();
        } else {
            closeConnection();
        }

        return () => {
            closeConnection();
        };
    }, [isAuthenticated, connect, closeConnection]);

    // Tab visibility: disconnect after 10 min hidden, reconnect when visible
    useEffect(() => {
        if (isLocalDev() || !WEBSOCKET_URL) return;

        const handleVisibility = () => {
            if (document.hidden) {
                hiddenAtRef.current = Date.now();
                visibilityTimeoutRef.current = setTimeout(() => {
                    if (wsRef.current?.readyState === WebSocket.OPEN) {
                        intentionalCloseRef.current = true;
                        wsRef.current.close(1000, 'tab_hidden');
                        wsRef.current = null;
                        if (heartbeatRef.current) {
                            clearInterval(heartbeatRef.current);
                            heartbeatRef.current = null;
                        }
                    }
                }, WS_VISIBILITY_DISCONNECT_MS);
            } else {
                hiddenAtRef.current = null;
                if (visibilityTimeoutRef.current) {
                    clearTimeout(visibilityTimeoutRef.current);
                    visibilityTimeoutRef.current = null;
                }
                if (isAuthenticated && !wsRef.current) {
                    attemptRef.current = 0;
                    connect();
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibility);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibility);
        };
    }, [isAuthenticated, connect]);
}
